import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { TriangleAlert } from 'lucide-react'
import { lazy, Suspense, useState, type ComponentType } from 'react'
import { Route, Routes, useNavigate } from 'react-router'
import { Toaster } from 'sonner'

import { AppShell } from '@/components/AppShell'
import { CommandPalette } from '@/components/CommandPalette'
import { ShortcutHelp } from '@/components/ShortcutHelp'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { OverviewPage } from '@/features/overview/OverviewPage'
import { VaultSetup, VaultUnlock } from '@/features/vault/VaultGate'
import { useServerEvents } from '@/hooks/useServerEvents'
import { useShortcuts } from '@/hooks/useShortcuts'
import { ApiRequestError, fetchHealth, fetchVaultStatus, lockVault } from '@/lib/api'
import { useConnectionStore } from '@/store/connections'

/**
 * Jede Seite außer der Startseite wird erst beim Betreten geladen.
 *
 * Vorher lag alles in einem Brocken: Der Anmeldebildschirm hat das Terminal (xterm) und die
 * Diagramme (recharts) mitgeladen, bevor überhaupt klar war, ob jemand hereinkommt. Die
 * Übersicht bleibt bewusst statisch — sie ist das Ziel nach jedem Anmelden, und ein Ladebalken
 * direkt nach dem Passwort wäre ein Rückschritt.
 */
function page<T extends Record<string, ComponentType>>(
  loader: () => Promise<T>,
  name: keyof T,
): ComponentType {
  return lazy(() => loader().then((module) => ({ default: module[name] as ComponentType })))
}

const ServerList = page(() => import('@/features/servers/ServerList'), 'ServerList')
const TerminalPage = page(() => import('@/features/terminal/TerminalPage'), 'TerminalPage')
const FilesPage = page(() => import('@/features/files/FilesPage'), 'FilesPage')
const ProcessesPage = page(() => import('@/features/processes/ProcessesPage'), 'ProcessesPage')
const SslPage = page(() => import('@/features/ssl/SslPage'), 'SslPage')
const ReachabilityPage = page(
  () => import('@/features/reachability/ReachabilityPage'),
  'ReachabilityPage',
)
const ChecklistPage = page(() => import('@/features/checklist/ChecklistPage'), 'ChecklistPage')
const SecurityPage = page(() => import('@/features/security/SecurityPage'), 'SecurityPage')
const ProjectsPage = page(() => import('@/features/projects/ProjectsPage'), 'ProjectsPage')
const ProjectDeployPage = page(
  () => import('@/features/projects/ProjectDeployPage'),
  'ProjectDeployPage',
)
const BackupsPage = page(() => import('@/features/backups/BackupsPage'), 'BackupsPage')
const AlertsPage = page(() => import('@/features/alerts/AlertsPage'), 'AlertsPage')
const ReportsPage = page(() => import('@/features/reports/ReportsPage'), 'ReportsPage')
const SnippetsPage = page(() => import('@/features/snippets/SnippetsPage'), 'SnippetsPage')
const SettingsPage = page(() => import('@/features/settings/SettingsPage'), 'SettingsPage')

function TransportWarning() {
  const health = useQuery({ queryKey: ['health'], queryFn: fetchHealth })
  if (!health.data?.transport.insecure) return null

  /*
   * Quittiert heißt: bewusst so gewollt. Dann bleibt ein Hinweis stehen, aber kein roter Alarm —
   * ein Banner, das bei jedem Seitenaufruf schreit, obwohl man es selbst eingeschaltet hat,
   * erzieht einen nur dazu, rote Banner zu übersehen.
   */
  if (health.data.transport.acknowledged) {
    return (
      <p className="text-muted-foreground flex items-center gap-2 text-xs">
        <TriangleAlert className="text-status-warn size-3.5 shrink-0" />
        Ohne TLS erreichbar — mit KONTOR_ALLOW_INSECURE=1 bewusst quittiert.
      </p>
    )
  }

  return (
    <Alert variant="destructive">
      <TriangleAlert />
      <AlertTitle>Unverschlüsselte Verbindung</AlertTitle>
      <AlertDescription>
        Dieses Backend ist von außen erreichbar und läuft ohne TLS davor. Stelle nginx mit HTTPS
        davor, bevor du echte Zugangsdaten anlegst.
      </AlertDescription>
    </Alert>
  )
}

export function App() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const vault = useQuery({ queryKey: ['vault-status'], queryFn: fetchVaultStatus })
  // Gated on , not just on the vault being open: the vault is global state, a
  // session is per client, and a second device must see the unlock screen rather than an empty
  // dashboard whose every request 401s.
  const unlocked = vault.data?.authenticated === true

  useServerEvents(unlocked)

  const lock = useMutation({
    mutationFn: lockVault,
    onSuccess: () => {
      useConnectionStore.getState().clear()
      void queryClient.invalidateQueries({ queryKey: ['vault-status'] })
    },
  })

  // Registered unconditionally: hooks cannot be conditional, and the handlers below are harmless
  // while the vault is locked -- the gate screens are rendered instead of the app anyway.
  useShortcuts({
    openPalette: () => {
      if (unlocked) setPaletteOpen(true)
    },
    showHelp: () => {
      if (unlocked) setHelpOpen(true)
    },
    lock: () => {
      if (unlocked) lock.mutate()
    },
    goto: (path) => {
      if (unlocked) void navigate(path)
    },
  })

  if (vault.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <Skeleton className="h-40 w-full max-w-md" />
      </div>
    )
  }

  if (vault.isError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <Alert variant="destructive" className="max-w-md">
          <TriangleAlert />
          <AlertTitle>Backend nicht erreichbar</AlertTitle>
          <AlertDescription>
            {vault.error instanceof ApiRequestError
              ? vault.error.message
              : 'Unerwarteter Fehler beim Abruf des Vault-Status.'}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!vault.data.initialized) {
    return (
      <>
        <VaultSetup />
        <Toaster position="bottom-right" />
      </>
    )
  }

  if (!vault.data.authenticated) {
    return (
      <>
        <VaultUnlock />
        <Toaster position="bottom-right" />
      </>
    )
  }

  return (
    <AppShell onSearch={() => setPaletteOpen(true)}>
      <TransportWarning />
      {/* Eine Fläche in Seitengröße statt eines Spinners: der Umriss der Seite steht sofort. */}
      <Suspense fallback={<Skeleton className="h-96 w-full" />}>
        <Routes>
          <Route path="/" element={<OverviewPage />} />
          <Route path="/server" element={<ServerList />} />
          <Route path="/server/:serverId/terminal" element={<TerminalPage />} />
          <Route path="/server/:serverId/dateien" element={<FilesPage />} />
          <Route path="/server/:serverId/prozesse" element={<ProcessesPage />} />
          <Route path="/server/:serverId/ssl" element={<SslPage />} />
          <Route path="/server/:serverId/erreichbar" element={<ReachabilityPage />} />
          <Route path="/server/:serverId/checkliste" element={<ChecklistPage />} />
          <Route path="/server/:serverId/sicherheit" element={<SecurityPage />} />
          <Route path="/projekte" element={<ProjectsPage />} />
          <Route path="/projekte/:projectId" element={<ProjectDeployPage />} />
          <Route path="/backups" element={<BackupsPage />} />
          <Route path="/alarme" element={<AlertsPage />} />
          <Route path="/berichte" element={<ReportsPage />} />
          <Route path="/snippets" element={<SnippetsPage />} />
          <Route path="/einstellungen" element={<SettingsPage />} />
          {/* Anything unknown falls back to the overview rather than a dead end. */}
          <Route path="*" element={<OverviewPage />} />
        </Routes>
      </Suspense>

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onLock={() => lock.mutate()}
        onShowHelp={() => setHelpOpen(true)}
      />
      <ShortcutHelp open={helpOpen} onOpenChange={setHelpOpen} />
      <Toaster position="bottom-right" />
    </AppShell>
  )
}
